import React, { useState, useEffect, forwardRef } from "react";
import { Line } from "react-chartjs-2";
import { FaSyncAlt } from "react-icons/fa";
import api, { MARKET_BASE } from '../../utils/apiConfig';

import logger from '../../utils/logger';
import Modal from "../shared/Modal";

const TABS = [
  { key: "performance", label: "Performance" },
  { key: "financials", label: "Financials" },
  { key: "earnings", label: "Earnings" },
  { key: "allocations", label: "Allocations" },
  { key: "ai", label: "AI Insights" },
];

const MOCK_DATA = {
  symbol: "AAPL",
  name: "Apple Inc.",
  logo: "https://logo.clearbit.com/apple.com",
  sector: "Technology",
  industry: "Consumer Electronics",
  financials: {
    revenue: "$394B",
    netIncome: "$99B",
    eps: "$6.11",
    peRatio: "28.5",
    cash: "$62B",
    debt: "$98B",
  },
  earnings: [
    { date: "2025-03-29", type: "Q2 2025", eps: "$1.52", surprise: "+0.03" },
    {
      date: "2025-07-31",
      type: "Q3 2025 (Est)",
      eps: "$1.20",
      surprise: "N/A",
    },
  ],
  allocations: [
    { level: "Firm", percent: 4.2 },
    { level: "Advisor", percent: 6.1 },
    { level: "Household", percent: 8.7 },
    { level: "Account", percent: 12.3 },
  ],
  aiInsights: {
    pros: [
      "Strong brand and global presence",
      "Consistent revenue growth",
      "Robust cash flow and balance sheet",
      "Innovative product pipeline",
    ],
    cons: [
      "High dependence on iPhone sales",
      "Supply chain risks",
      "Premium valuation",
      "Regulatory scrutiny in multiple regions",
    ],
    summary:
      "Apple Inc. is a global technology leader with a strong brand, robust financials, and a history of innovation. Investors should consider its premium valuation and product concentration risks.",
  },
};

const TIME_RANGES = [
  { label: "3Y", value: "3y", days: 365 * 3 },
  { label: "1Y", value: "1y", days: 365 },
  { label: "6M", value: "6m", days: 30 * 6 },
  { label: "3M", value: "3m", days: 30 * 3 },
  { label: "1M", value: "1m", days: 30 },
];

function getCutoffDate(range) {
  const now = new Date();
  switch (range) {
    case "3y":
      return new Date(now.getFullYear() - 3, now.getMonth(), now.getDate());
    case "1y":
      return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    case "6m":
      return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    case "3m":
      return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    case "1m":
      return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    default:
      return null;
  }
}

function getPerformance(priceData, days) {
  if (!priceData || priceData.length < 2) return null;
  const lastIdx = priceData.length - 1;
  const lastPrice = priceData[lastIdx][1];
  let refIdx = lastIdx;
  const refDate = new Date(priceData[lastIdx][0]);
  for (let i = lastIdx - 1; i >= 0; --i) {
    const d = new Date(priceData[i][0]);
    if ((refDate - d) / (1000 * 60 * 60 * 24) >= days) {
      refIdx = i;
      break;
    }
  }
  const refPrice = priceData[refIdx][1];
  if (refPrice === 0) return null;
  return ((lastPrice - refPrice) / refPrice) * 100;
}

// Cache for live AAPL data
let liveAAPLCache = null;

const SymbolDetailModal = forwardRef(function SymbolDetailModal(props, ref) {
  const { open, onClose, symbol, onTabChange } = props;
  
  const [tab, setTab] = useState("performance");
  const [watchlist, setWatchlist] = useState(false);
  const [rnd, setRnd] = useState(false);
  const [priceData, setPriceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("3y");
  const [hoveredRange, setHoveredRange] = useState(null);
  const [liveAAPL, setLiveAAPL] = useState(liveAAPLCache);
  const [liveError, setLiveError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showRefreshTooltip, setShowRefreshTooltip] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Add spotlight effect that fires on modal open or tab change
  useEffect(() => {
    if (!open) return;
    const target = document.querySelector('[data-demo-target="symbol-detail-modal"]');
    if (target) {
      window.dispatchEvent(new CustomEvent("spotlight", {
        detail: {
          target,
          label: "Click the neon X to close and continue.",
          showCloseButton: true
        }
      }));
    }
  }, [tab, open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch("/data/all_holdings_weekly_closes.json")
      .then((r) => r.json())
      .then((json) => {
        const arr = json[symbol] || [];
        setPriceData(arr);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [symbol, open]);

  useEffect(() => {
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(darkModeMediaQuery.matches);
    
    const handleChange = (e) => setIsDarkMode(e.matches);
    darkModeMediaQuery.addEventListener('change', handleChange);
    
    return () => darkModeMediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Helper to fetch live AAPL price
  const fetchLiveAAPL = async () => {
    setRefreshing(true);
    setLiveError(null);
    try {
      const { data: json } = await api.get(`${MARKET_BASE}/yahoo/AAPL`);
      logger.info("AAPL live API response:", json); // Debug log
      if (!json.chart || !json.chart.result || !json.chart.result[0])
        throw new Error("Malformed API response");
      const result = json.chart.result[0];
      const close = result.indicators.quote[0].close;
      const lastIdx = close.length - 1;
      let lastPrice = close[lastIdx];
      // If lastPrice is null, try previous value
      if (lastPrice == null && lastIdx > 0) lastPrice = close[lastIdx - 1];
      const oneWeekIdx = lastIdx - 5 >= 0 ? lastIdx - 5 : 0;
      const oneMonthIdx = 0;
      const perf1W = close[oneWeekIdx]
        ? ((lastPrice - close[oneWeekIdx]) / close[oneWeekIdx]) * 100
        : null;
      const perf1M = close[oneMonthIdx]
        ? ((lastPrice - close[oneMonthIdx]) / close[oneMonthIdx]) * 100
        : null;
      liveAAPLCache = {
        lastPrice,
        perf1W,
        perf1M,
      };
      setLiveAAPL(liveAAPLCache);
      setLiveError(null);
    } catch (err) {
      setLiveError("Could not fetch live price for AAPL.");
      logger.error("AAPL live price fetch error:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch live AAPL price and 1W/1M return only once and cache
  useEffect(() => {
    if (symbol !== "AAPL" || liveAAPLCache) return;
    fetchLiveAAPL();
    // eslint-disable-next-line
  }, [symbol]);

  // Filter price data for selected range
  let filtered = priceData;
  if (filtered.length > 0) {
    const cutoff = getCutoffDate(range);
    if (cutoff) {
      filtered = filtered.filter(([date]) => new Date(date) >= cutoff);
    }
  }

  // Chart data
  const chartData = {
    labels: filtered.map(([date]) => date),
    datasets: [
      {
        label: `${symbol} Price`,
        data: filtered.map(([, price]) => price),
        borderColor: isDarkMode ? "#60A5FA" : "#1E3A8A",
        backgroundColor: isDarkMode ? "rgba(96, 165, 250, 0.1)" : "rgba(30, 58, 138, 0.1)",
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 0,
        borderWidth: 2,
        pointBackgroundColor: isDarkMode ? "#60A5FA" : "#1E3A8A",
        pointHoverBackgroundColor: isDarkMode ? "#93C5FD" : "#2563EB",
      },
    ],
  };
  const chartOptions = {
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(75, 85, 99, 0.3)',
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: (ctx) => {
            const idx = ctx.dataIndex;
            const startVal = filtered[0]?.[1];
            const currVal = filtered[idx]?.[1];
            const pct =
              startVal !== 0 ? ((currVal - startVal) / startVal) * 100 : null;
            return [
              `Price: $${currVal?.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
              pct !== null
                ? `Return: ${pct >= 0 ? "+" : ""}${pct?.toFixed(2)}%`
                : "",
            ];
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: isDarkMode ? 'rgba(75, 85, 99, 0.1)' : 'rgba(75, 85, 99, 0.1)',
          borderColor: isDarkMode ? 'rgba(75, 85, 99, 0.2)' : 'rgba(75, 85, 99, 0.2)',
        },
        ticks: {
          color: isDarkMode ? '#D1D5DB' : '#4B5563',
          maxRotation: 45,
          minRotation: 45,
          source: 'data',
          autoSkip: true,
          maxTicksLimit: 8,
          font: {
            size: 10
          },
          callback: (val, idx) => {
            const date = new Date(filtered[idx][0]);
            return date.toLocaleDateString("en-US", { 
              month: "short",
              year: "2-digit"
            });
          },
        }
      },
      y: {
        grid: {
          color: isDarkMode ? 'rgba(75, 85, 99, 0.1)' : 'rgba(75, 85, 99, 0.1)',
          borderColor: isDarkMode ? 'rgba(75, 85, 99, 0.2)' : 'rgba(75, 85, 99, 0.2)',
        },
        ticks: {
          color: isDarkMode ? '#D1D5DB' : '#4B5563',
          font: {
            size: 10
          },
          callback: (v) =>
            `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
        },
      },
    },
  };

  // Calculate summary card price and performance
  let lastPrice =
    priceData.length > 0 ? priceData[priceData.length - 1][1] : null;
  let perf1W = getPerformance(priceData, 7);
  let perf1M = getPerformance(priceData, 30);
  if (symbol === "AAPL" && liveAAPL) {
    lastPrice = liveAAPL.lastPrice;
    perf1W = liveAAPL.perf1W;
    perf1M = liveAAPL.perf1M;
  }

  // For hover performance
  const getPerfForRange = (rangeObj) => {
    if (!priceData.length) return null;
    return getPerformance(priceData, rangeObj.days);
  };

  // Update chartOptions with dark mode
  const finalChartOptions = {
    ...chartOptions,
    darkMode: isDarkMode,
  };

  if (!open) return null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      maxWidth="3xl"
      ref={ref}
    >
      <div 
        className="bg-surface-default dark:bg-surface-dark rounded-lg w-full relative overflow-x-hidden"
        ref={ref}
        data-demo-target="symbol-detail-modal"
      >
        {/* Summary Card */}
        <div className="flex items-center gap-4 px-4 py-6 border-b border-gray-200 dark:border-gray-700">
          <img
            src={MOCK_DATA.logo}
            alt={MOCK_DATA.name + " logo"}
            className="w-14 h-14 rounded object-contain bg-transparent"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg";
            }}
          />
          <div className="flex-1">
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {MOCK_DATA.name}{" "}
              <span className="text-gray-500 dark:text-gray-400 text-base font-normal">
                ({MOCK_DATA.symbol})
              </span>
            </div>
            <div className="text-gray-600 dark:text-gray-400 text-sm mb-1">
              {MOCK_DATA.sector} &bull; {MOCK_DATA.industry}
            </div>
            <div className="text-2xl font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2">
              {lastPrice !== null
                ? `$${lastPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                : "--"}
              {/* Inline refresh icon for AAPL with tooltip */}
              {symbol === "AAPL" && (
                <span className="relative">
                  <button
                    onClick={fetchLiveAAPL}
                    disabled={refreshing}
                    aria-label="Refresh Price"
                    className={`ml-1 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition ${refreshing ? "opacity-60" : ""}`}
                    style={{ lineHeight: 0, height: 24 }}
                    onMouseEnter={() => setShowRefreshTooltip(true)}
                    onMouseLeave={() => setShowRefreshTooltip(false)}
                  >
                    <FaSyncAlt
                      size={16}
                      className={`${refreshing ? "animate-spin" : ""} text-blue-700 dark:text-blue-400`}
                      style={{ verticalAlign: "middle" }}
                    />
                  </button>
                  {/* Tooltip */}
                  {showRefreshTooltip && !refreshing && (
                    <div className="absolute left-1/2 -translate-x-1/2 -top-7 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded px-2 py-1 shadow-lg dark:shadow-none z-10 whitespace-nowrap">
                      Refresh price
                    </div>
                  )}
                </span>
              )}
            </div>
            <div className="flex gap-3 mt-1">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  perf1W > 0
                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                    : perf1W < 0
                    ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                }`}
              >
                1W:{" "}
                {perf1W !== null
                  ? `${perf1W > 0 ? "+" : ""}${perf1W.toFixed(2)}%`
                  : "--"}
              </span>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${
                  perf1M > 0
                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                    : perf1M < 0
                    ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                }`}
              >
                1M:{" "}
                {perf1M !== null
                  ? `${perf1M > 0 ? "+" : ""}${perf1M.toFixed(2)}%`
                  : "--"}
              </span>
            </div>
            {/* Error message for live price */}
            {symbol === "AAPL" && liveError && (
              <div className="mt-2 text-xs text-red-600 dark:text-red-400 font-semibold">
                {liveError}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              className={`px-3 py-1 rounded text-sm font-medium border transition ${
                watchlist
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-surface-default dark:bg-surface-dark text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              }`}
              onClick={() => setWatchlist((w) => !w)}
            >
              {watchlist ? "Added to Watchlist" : "Add to Watchlist"}
            </button>
            <button
              className={`px-3 py-1 rounded text-sm font-medium border transition ${
                rnd
                  ? "bg-green-600 text-white border-green-600"
                  : "bg-surface-default dark:bg-surface-dark text-green-600 dark:text-green-400 border-green-600 dark:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20"
              }`}
              onClick={() => setRnd((r) => !r)}
            >
              {rnd ? "Added to R&D" : "Add to R&D"}
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`px-3 py-2 text-xs font-medium focus:outline-none transition ${
                tab === t.key
                  ? "border-b-2 border-blue-600 text-blue-700 dark:text-blue-400 bg-surface-default dark:bg-surface-dark"
                  : "text-gray-600 dark:text-gray-400 hover:text-blue-700 dark:hover:text-blue-400"
              }`}
              onClick={() => {
                setTab(t.key);
                onTabChange?.(t.key);
              }}
              aria-selected={tab === t.key}
              role="tab"
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-6">
          {tab === "performance" && (
            <div>
              {/* Toggles: match PerformanceChart styles */}
              <div className="flex space-x-1 mb-2">
                {TIME_RANGES.map((opt) => {
                  const perf = getPerfForRange(opt);
                  const showPerf = hoveredRange === opt.value;
                  return (
                    <span
                      key={opt.value}
                      className="relative flex flex-col items-center"
                      style={{ minWidth: 40 }}
                    >
                      <button
                        onClick={() => setRange(opt.value)}
                        onMouseEnter={() => setHoveredRange(opt.value)}
                        onMouseLeave={() => setHoveredRange(null)}
                        className={`
                          w-full px-2 py-[2px] text-[10px] rounded-full transition-all
                          ${range === opt.value
                            ? "bg-blue-600 text-white dark:bg-gray-800 dark:text-emerald-400 dark:border-2 dark:border-emerald-500/50 dark:shadow-[0_0_10px_-3px_rgba(16,185,129,0.5)]"
                            : "bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700 dark:border dark:border-gray-700"}
                        `}
                      >
                        {opt.label}
                      </button>
                      {showPerf && perf !== null && (
                        <span
                          className={`absolute -top-7 right-0 px-2 py-0.5 rounded text-xs font-semibold shadow-lg dark:shadow-none ${
                            perf > 0
                              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                              : perf < 0
                              ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                          } border border-gray-200 dark:border-gray-700 backdrop-blur-sm`}
                          style={{
                            minWidth: 54,
                            textAlign: "center",
                            zIndex: 10,
                          }}
                        >
                          {perf > 0 ? "+" : ""}
                          {perf.toFixed(2)}%
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
              {/* Chart container */}
              <div className="h-[250px] bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                {loading ? (
                  <div className="flex items-center justify-center h-full text-gray-600 dark:text-gray-400">
                    <div className="animate-pulse">Loading chart…</div>
                  </div>
                ) : (
                  <Line data={chartData} options={finalChartOptions} />
                )}
              </div>
            </div>
          )}
          {tab === "financials" && (
            <div>
              <h3 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">Key Financials</h3>
              <ul className="grid grid-cols-2 gap-4 text-sm">
                <li className="text-gray-900 dark:text-gray-100">
                  <span className="text-gray-600 dark:text-gray-400">Revenue:</span>{" "}
                  {MOCK_DATA.financials.revenue}
                </li>
                <li className="text-gray-900 dark:text-gray-100">
                  <span className="text-gray-600 dark:text-gray-400">Net Income:</span>{" "}
                  {MOCK_DATA.financials.netIncome}
                </li>
                <li className="text-gray-900 dark:text-gray-100">
                  <span className="text-gray-600 dark:text-gray-400">EPS:</span>{" "}
                  {MOCK_DATA.financials.eps}
                </li>
                <li className="text-gray-900 dark:text-gray-100">
                  <span className="text-gray-600 dark:text-gray-400">P/E Ratio:</span>{" "}
                  {MOCK_DATA.financials.peRatio}
                </li>
                <li className="text-gray-900 dark:text-gray-100">
                  <span className="text-gray-600 dark:text-gray-400">Cash:</span>{" "}
                  {MOCK_DATA.financials.cash}
                </li>
                <li className="text-gray-900 dark:text-gray-100">
                  <span className="text-gray-600 dark:text-gray-400">Debt:</span>{" "}
                  {MOCK_DATA.financials.debt}
                </li>
              </ul>
            </div>
          )}
          {tab === "earnings" && (
            <div>
              <h3 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">Earnings</h3>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 dark:text-gray-400">
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">EPS</th>
                    <th className="py-2 pr-4">Surprise</th>
                  </tr>
                </thead>
                <tbody className="text-gray-900 dark:text-gray-100">
                  {MOCK_DATA.earnings.map((e, i) => (
                    <tr key={i} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="py-2 pr-4">{e.date}</td>
                      <td className="py-2 pr-4">{e.type}</td>
                      <td className="py-2 pr-4">{e.eps}</td>
                      <td className="py-2 pr-4">{e.surprise}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tab === "allocations" && (
            <div>
              <h3 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">Portfolio Allocations</h3>
              <ul className="text-sm">
                {MOCK_DATA.allocations.map((a, i) => (
                  <li key={i} className="mb-1 text-gray-900 dark:text-gray-100">
                    <span className="text-gray-600 dark:text-gray-400">{a.level}:</span>{" "}
                    {a.percent}%
                  </li>
                ))}
              </ul>
            </div>
          )}
          {tab === "ai" && (
            <div>
              <h3 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">AI-Generated Insights</h3>
              <div className="mb-2">
                <span className="text-green-700 dark:text-green-400 font-medium">Pros:</span>
                <ul className="list-disc ml-6 text-gray-900 dark:text-gray-100">
                  {MOCK_DATA.aiInsights.pros.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
              <div className="mb-2">
                <span className="text-red-700 dark:text-red-400 font-medium">Cons:</span>
                <ul className="list-disc ml-6 text-gray-900 dark:text-gray-100">
                  {MOCK_DATA.aiInsights.cons.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-4">
                {MOCK_DATA.aiInsights.summary}
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
});

export default SymbolDetailModal;
